/**
 * TextQuestion Component
 * Handles text, email, tel, and postcode input types
 */

interface TextQuestionProps {
  questionId: string
  questionType: 'text' | 'email' | 'tel' | 'postcode'
  value: string
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
  error?: string
}

const TextQuestion = ({
  questionId,
  questionType,
  value,
  onChange,
  placeholder = '',
  required = false,
  error
}: TextQuestionProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }

  const getInputType = () => {
    switch (questionType) {
      case 'email':
        return 'email'
      case 'tel':
        return 'tel'
      case 'postcode':
      case 'text':
      default:
        return 'text'
    }
  }

  const getPlaceholder = () => {
    if (placeholder) return placeholder

    switch (questionType) {
      case 'email':
        return 'your.email@example.com'
      case 'tel':
        return '07123 456789'
      case 'postcode':
        return 'SW1A 1AA'
      case 'text':
      default:
        return 'Type your answer here...'
    }
  }

  return (
    <div className="w-full max-w-xl">
      <input
        type={getInputType()}
        id={questionId}
        name={questionId}
        value={value}
        onChange={handleChange}
        placeholder={getPlaceholder()}
        required={required}
        autoComplete={questionType === 'email' ? 'email' : questionType === 'tel' ? 'tel' : 'off'}
        autoFocus
        className={`
          w-full px-6 py-4 text-lg border-2 rounded-lg transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-primary-green focus:ring-opacity-50
          ${
            error
              ? 'border-red-500 bg-red-50'
              : 'border-gray-300 bg-white focus:border-primary-green'
          }
        `}
        aria-invalid={!!error}
        aria-describedby={error ? `${questionId}-error` : undefined}
      />

      {/* Error Message */}
      {error && (
        <div
          id={`${questionId}-error`}
          className="mt-2 text-sm text-red-600 flex items-center gap-1"
          role="alert"
        >
          <svg
            className="w-4 h-4"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </div>
      )}
    </div>
  )
}

export default TextQuestion
